import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useBack } from '../../hooks/useBack';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiClient, getApiErrorMessage, getBlobApiErrorMessage } from '../../api/client';
import type { ApiResponse } from '../../types/api-manifest';
import { useAuth } from '../../contexts/AuthContext';
import { invalidateTickets } from '../../lib/tickets-invalidation';
import { canApproveTickets, canAttachCustomerSignedCopy, getRoleLabel } from '../../lib/roles';
import { isSalesRole } from '../../lib/sales-access';
import { parseTicketStampFile } from '../../lib/approval-stamp';
import AttachSignedCopyDialog from '../../components/tickets/AttachSignedCopyDialog';
import { setQueryDataMapped } from '../../lib/queryClient';
import { unwrap } from '../../lib/api-envelope';
import { SuppressedRecipientNotice, suppressedRefusalOf, type SuppressedRefusal } from '../../components/SuppressedRecipientNotice';
import { fmtLineItemType, fmtUnit, fmtQty, fmtCurrency, parseMoney, crewRoleLabel } from '../../lib/display-labels';
import { getJobTypeLabel } from '../../constants/job-types';
import { formatTicketHours, hasContradictoryTimes } from '../../lib/ticket-hours';
import { formatCalendarDate, formatInstant, formatInstantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { filenameFromContentDisposition } from '../../lib/content-disposition';
import { disposePrintFrame } from '../../lib/blob-tab';
import { runTicketPdfAction } from '../../lib/ticket-pdf';
import {
  ticketPdfNeedsPreview,
  ticketEditIsFrozen,
  ticketDeleteIsBlocked,
  officeTicketStatusLabel,
  ticketIsInvoiceable,
} from '../../lib/ticket-status';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from '../../lib/load-failure';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import CustomerEmailPromptModal from '../../components/tickets/CustomerEmailPromptModal';
import MoveTicketJobModal from '../../components/tickets/MoveTicketJobModal';
import { SendModalSavedContacts, useSendModalContacts, isSavedContact } from '../../components/tickets/SendModalSavedContacts';
import MoveTicketJobOutcome from '../../components/tickets/MoveTicketJobOutcome';
import { BillingFlagsPanel, BILLING_FLAGS_PANEL_ID } from '../../components/tickets/BillingFlagsPanel';
import { PriceBookGateNotice } from '../../components/tickets/PriceBookGateNotice';
import { readPriceBookRefusal, readPriceBookWarning, type PriceBookUnlinkedLine } from '../../lib/price-book-link';
import { openBillingFlagCount, useTicketBillingFlags } from '../../hooks/useTicketBillingFlags';
import {
  canMoveTicketJob,
  moveJobIsFrozen,
  ticketNumberModeIsDerived,
  type MoveJobResult,
} from '../../lib/ticket-move-job';
import EmailEngagementPill from '../../components/EmailEngagementPill';
import { Tooltip } from '../../components/ui/tooltip';
import { useTicketEngagementPolling } from '../../hooks/useTicketEngagementPolling';
import {
  CheckCircle,
  Send,
  Loader2,
  Mail,
  FileText,
  Calendar,
  Clock,
  Wrench,
  MapPin,
  DollarSign,
  Pencil,
  XCircle,
  User,
  Building2,
  Users,
  Archive,
  ArchiveRestore,
  Trash2,
  Eye,
  Printer,
  Download,
  RefreshCw,
  AlertTriangle,
  Copy,
  Paperclip,
  ArrowLeftRight,
} from 'lucide-react';

/**
 * THE STATUS BADGE — saturated fill, dark ink, and an em dash for anything
 * outside the map.
 *
 * Five signal hues and no sixth, grouped by what the office has to do:
 *
 *   slate   nothing is in play — draft, its two legacy spellings, archived
 *   amber   the OFFICE must act — field_approved
 *   blue    out with the customer, waiting — office_approved, legacy `approved`
 *   green   no further office action — customer_approved, invoiced
 *   red     it came back — rejected, customer_rejected
 *
 * `text-fi-signal-ink` on a `bg-fi-signal-*` fill, because the signal hues are
 * declared once and do not follow the theme: one dark ink holds on all five in
 * both halves. The pale 10%-wash-plus-bright-ink shape this replaced put
 * red-400 ink on a near-white panel in light mode. (Described rather than
 * spelled: the Tailwind scanner has no syntax and reads a comment as source,
 * so writing the class here would mint the very rule this replaced.)
 *
 * A VALUE OUTSIDE THIS MAP READS `—` ON SLATE. It used to fall back to Draft,
 * which on a ticket is a claim that nobody has submitted work that may well
 * have been invoiced.
 *
 * THE LABELS ARE THIS PAGE'S OWN AND ARE DELIBERATELY NOT UNIFIED HERE.
 * `submitted` reads "Pending" here, "Draft" on the register
 * (`screens/TicketsScreen.tsx`, fe#730) and "Field Approved" in
 * `lib/ticket-status.ts`. That three-way disagreement predates this change, it
 * is documented in both of those files, and settling it is a decision about
 * vocabulary rather than paint — and the register is a protected file, so the
 * shared map that would settle it cannot be introduced from here. The SKIN is
 * what this change unifies; the words are left exactly where they were.
 */
const STATUS_BADGE: Record<string, { label: string; icon: any; fill: string }> = {
  draft:             { label: 'Draft',             icon: Pencil,      fill: 'bg-fi-signal-slate' },
  active:            { label: 'Draft',             icon: Pencil,      fill: 'bg-fi-signal-slate' },
  submitted:         { label: 'Pending',           icon: Send,        fill: 'bg-fi-signal-slate' },
  field_approved:    { label: 'Field Approved',    icon: CheckCircle, fill: 'bg-fi-signal-amber' },
  approved:          { label: 'Approved',          icon: CheckCircle, fill: 'bg-fi-signal-blue' },
  office_approved:   { label: 'Office Approved',   icon: CheckCircle, fill: 'bg-fi-signal-blue' },
  customer_approved: { label: 'Customer Approved', icon: CheckCircle, fill: 'bg-fi-signal-green' },
  customer_rejected: { label: 'Customer Rejected', icon: XCircle,     fill: 'bg-fi-signal-red' },
  rejected:          { label: 'Rejected',          icon: XCircle,     fill: 'bg-fi-signal-red' },
  invoiced:          { label: 'Invoiced',          icon: CheckCircle, fill: 'bg-fi-signal-green' },
  archived:          { label: 'Archived',          icon: Archive,     fill: 'bg-fi-signal-slate' },
};

const UNKNOWN_STATUS = { label: '\u2014', icon: AlertTriangle, fill: 'bg-fi-signal-slate' };

const statusSkin = (status: string | null | undefined) =>
  (status && STATUS_BADGE[status]) || UNKNOWN_STATUS;


/**
 * A record panel, in the register idiom: one amber 4px left edge on the PANEL,
 * an eyebrow and an `<h2>`, and the rows inside it.
 *
 * The four sections below used a heading floating above an unbordered card, so
 * a reader scrolling the page met four unrelated boxes. This is the same shape
 * the Quotes and Rate Sheets registers use, and nothing about a row changed to
 * get it.
 */
function RecordPanel({
  eyebrow,
  title,
  count,
  children,
}: {
  eyebrow: string;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card">
      {/* A div, not a <header>: inside a <section> the element still maps to
          the banner role in some accessibility trees, and four banners on a
          page is not what a register header is. */}
      <div className="border-b border-fieldiq-border px-4 py-3">
        <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">{eyebrow}</p>
        <h2 className="mt-1 text-base font-extrabold tracking-tight text-white">
          {title}
          {count !== undefined && <span className="ml-2 font-bold text-fieldiq-muted">{count}</span>}
        </h2>
      </div>
      {children}
    </section>
  );
}

/**
 * THE BAND, IN ONE PLACE, because this page has four states and every one of
 * them has to wear it.
 *
 * The eyebrow is constant and the title is the ticket's own identity — this is
 * a detail page, so the band names the record rather than the register. It
 * carries the back arrow (a plain `ArrowLeft`, no visible word) and no
 * description: a description line has never been measured against the hero
 * circle's clearance, and neither reference page uses one.
 *
 * `stats` is optional and is OMITTED — not zeroed — while the ticket has not
 * arrived. A strip of `\u2014` on a page that is still loading states that the
 * ticket has no total; `loading` renders a pulse, and no strip at all is the
 * honest shape for a failure and for a row that does not exist.
 */
function TicketBand({
  title,
  onBack,
  actions,
  stats,
}: {
  title: string;
  onBack: () => void;
  actions?: React.ReactNode;
  stats?: React.ComponentProps<typeof PageHeaderBand>['stats'];
}) {
  return (
    <PageHeaderBand
      eyebrow="Casing operations"
      title={title}
      onBack={onBack}
      backLabel="Back to Field Tickets"
      actions={actions}
      stats={stats}
    />
  );
}

/** WHICH DESK HAS SIGNED — not which state the ticket is in, so no hue. */
const APPROVAL_MARK =
  'inline-flex items-center rounded-fi-pill border border-fieldiq-border px-2 py-1 text-[0.62rem] font-bold uppercase tracking-[0.06em] leading-none text-fieldiq-text';

/**
 * Line-for-line the same as fmtDate() in src/screens/TicketsScreen.tsx, and it
 * has to stay that way — TicketsScreen.parity.test.tsx renders both over one
 * payload precisely so the list and the detail cannot disagree about a ticket.
 * Both had the same UTC-midnight defect: a work_date of '2026-07-14' rendered
 * as "Jul 13, 2026" in Alberta.
 */
function fmtDate(d: any) {
  return formatCalendarDate(d) ?? '—';
}

/**
 * The tail of the "Email sent to …" toast when the office asked to save the
 * recipient (api#852's `contactSaved`). Exhaustive over the API's four values,
 * and every one of them follows a send that WENT — so none reads as a failed
 * send, including the save that failed. An absent or unknown value says the save
 * was not confirmed rather than guessing it happened.
 */
function sendContactSavedNote(contactSaved: unknown): string {
  switch (contactSaved) {
    case 'created': return '. Contact saved.';
    case 'existing': return '. Already a contact.';
    case 'failed': return ", but the contact couldn't be saved.";
    case 'no_customer': return '. This ticket has no client to save the contact to.';
    default: return ". The contact save wasn't confirmed.";
  }
}

/**
 * Day-rendering of a genuine INSTANT (`customer_approved_at` — full ISO-8601
 * with an offset). Not the same function as fmtDate above, and deliberately not
 * merged into it: an instant has to be converted. An approval at 21:30 on
 * Jul 14 in Alberta arrives as '2026-07-15T03:30:00Z', and only converting it
 * gets "Jul 14" — the calendar-day path would read the UTC prefix and say
 * "Jul 15". Converted in the TENANT's zone (companies.time_zone), not the
 * viewer's device.
 */
const fmtInstantDay = (d: any, timeZone: string | null) =>
  formatInstantDay(d, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';

/** An instant (sent / rejected) with its time, on the tenant's clock. */
function fmtDateTime(d: any, timeZone: string | null) {
  return formatInstant(d, timeZone, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }, 'en-US') ?? '—';
}

function mapTicket(t: any) {
  // Money the server did not send stays null so fmtCurrency renders '—'. A
  // priced-at-zero line and an unpriced one are different facts on an invoice.
  const taxRate1Amount = parseMoney(t.tax_rate_1_amount ?? t.taxRate1Amount);
  const taxRate2Amount = parseMoney(t.tax_rate_2_amount ?? t.taxRate2Amount);
  const subtotal = parseMoney(t.subtotal);
  const totalAmount = parseMoney(
    t.total_amount ?? t.totalAmount ?? t.total_price ?? t.totalPrice,
  );
  // Falling back to the sum is only meaningful if at least one part arrived;
  // summing three nulls would invent a $0.00 total.
  const derivedTotal =
    subtotal === null && taxRate1Amount === null && taxRate2Amount === null
      ? null
      : (subtotal ?? 0) + (taxRate1Amount ?? 0) + (taxRate2Amount ?? 0);
  return {
    id: t.id,
    ticketNumber: t.ticket_number || t.ticketNumber,
    jobId: t.job_id || t.jobId || '',
    jobNumber: t.job_number || t.jobNumber,
    status: t.status || 'draft',
    date: t.work_date || t.workDate || t.created_at || t.createdAt || '',
    customer: t.customer_name || t.customerName || 'Unknown',
    // The ticket's OWN customer snapshot, and `null` when it has none — NOT
    // `''`. The move-job picker scopes the destination list by it, and the
    // API's rule is `ticket.customer_id ?? sourceJob.customer_id`, so "this
    // ticket names no customer" and "we did not read one" have to stay
    // different values all the way down.
    customerId: t.customer_id ?? t.customerId ?? null,
    serviceType: t.job_type || t.jobType || '',
    // Server-computed. null (contradictory times) must not read as "0 hrs".
    hours: formatTicketHours(t.total_hours ?? t.totalHours),
    hoursContradictory: hasContradictoryTimes(
      t.work_date ?? t.workDate,
      t.end_date ?? t.endDate,
      t.start_time ?? t.startTime,
      t.end_time ?? t.endTime,
    ),
    total: totalAmount ?? derivedTotal,
    subtotal,
    taxRate1Label: t.tax_rate_1_label || t.taxRate1Label || null,
    taxRate1Amount,
    taxRate2Label: t.tax_rate_2_label || t.taxRate2Label || null,
    taxRate2Amount,
    lineItems: t.lineItems || t.line_items || [],
    equipment: t.equipment || [],
    crew: t.crew || [],
    customerContactName: t.customer_contact_name || t.customerContactName || null,
    customerContactPhone: t.customer_contact_phone || t.customerContactPhone || null,
    customerContactEmail: t.customer_contact_email || t.customerContactEmail || null,
    customerAddress: t.customer_address || t.customerAddress || null,
    companyName: t.company_name || t.companyName || null,
    companyAddress: t.company_address || t.companyAddress || null,
    companyPhone: t.company_phone || t.companyPhone || null,
    lsd: t.lsd || t.well_id_lsd || t.wellIdLsd || null,
    wellName: t.well_name || t.wellName || null,
    afeNumber: t.afe_number || t.afeNumber || null,
    wellLicence: t.well_licence || t.wellLicence || null,
    rigName: t.rig_name || t.rigName || null,
    // `rig_email` is real as of API 0391 (getTicketWithDetails aliases
    // j.rig_approval_email). `rig_consultant_email` / `rigConsultantEmail` were
    // read here for as long as this file has existed and have never been a
    // column, an alias or a payload key in either repo — a `||` chain long
    // enough that nobody checked whether any rung of it could match.
    rigEmail: t.rig_email ?? t.rigEmail ?? null,
    createdBy: t.created_by_name || t.createdByName || t.submitted_by_name || null,
    fieldApprovedAt: t.field_approved_at || t.fieldApprovedAt || null,
    sentToCustomerAt: t.sent_to_customer_at || t.sentToCustomerAt || null,
    customerApprovedAt: t.customer_approved_at || t.customerApprovedAt || null,
    customerApprovedByName: t.customer_approved_by_name || t.customerApprovedByName || null,
    // The customer approved by UPLOADING the ticket they signed, rather than
    // filling the stamp (api#721). `GET /tickets/:id` selects `t.*`, so this is
    // the whole jsonb record — filename, uploaded_at, r2_key, sha256, byte_size.
    // Only the filename is read here and the bytes are fetched through
    // `/tickets/:id/stamped-file`; an R2 URL must never be built from `r2_key`.
    // Presence of this object is the ONLY honest signal that a file exists —
    // see the note on `api.tickets.getStampedFile` for why its 404 is not one.
    customerStampFile: t.customer_stamp_file ?? t.customerStampFile ?? null,
    // The filled approval stamp (api#709). Read for its PRESENCE only, to hide
    // "Attach customer-signed copy": the API keeps a stamp and a file mutually
    // exclusive and answers 409 to an attach on a ticket holding either.
    hasCustomerStamp: (t.customer_stamp ?? t.customerStamp) != null,
    // TWO rejection columns, two different rejections, and they are not
    // interchangeable. `rejection_reason` is written by the office reject route
    // (`POST /tickets/:id/reject` → status `rejected`); `customer_rejection_reason`
    // is written by the customer approval route (→ status `customer_rejected`).
    // Neither route clears the other's column. Only the customer one was mapped,
    // so an office rejection's reason was unreachable from this screen and the
    // banner that claimed to show it read the customer's column instead.
    rejectionReason: t.rejection_reason || t.rejectionReason || null,
    customerRejectionReason: t.customer_rejection_reason || t.customerRejectionReason || null,
    customerRejectedAt: t.customer_rejected_at || t.customerRejectedAt || t.rejected_at || t.rejectedAt || null,
    customerRejectedByName: t.customer_rejected_by_name || t.customerRejectedByName || null,
    needsCustomerEmail: t.needs_customer_email ?? t.needsCustomerEmail ?? false,
    customerEmailStatus: t.customer_email_status || t.customerEmailStatus || 'not_sent',
    customerEmailLastAttemptAt: t.customer_email_last_attempt_at || t.customerEmailLastAttemptAt || null,
    customerEmailLastError: t.customer_email_last_error || t.customerEmailLastError || null,
    customerEmailDeliveredAt: t.customer_email_delivered_at || t.customerEmailDeliveredAt || null,
    customerEmailOpenCount: parseInt(t.customer_email_open_count || t.customerEmailOpenCount) || 0,
    customerEmailLastOpenedAt: t.customer_email_last_opened_at || t.customerEmailLastOpenedAt || null,
    customerEmailClickCount: parseInt(t.customer_email_click_count || t.customerEmailClickCount) || 0,
    customerEmailLastClickedAt: t.customer_email_last_clicked_at || t.customerEmailLastClickedAt || null,
    customerEmailComplainedAt: t.customer_email_complained_at || t.customerEmailComplainedAt || null,
    customerEmailSentTo: t.customer_email_sent_to || t.customerEmailSentTo || null,
    invoiceNumber: t.invoice_number || t.invoiceNumber || null,
    archivedAt: t.archived_at || t.archivedAt || null,
  };
}

// --- Document menu ---
//
// Two labels per action, because there are two documents. Until a ticket is
// office-approved the API will only render a watermarked preview stamped with
// the current status (`?preview=1`); the invoice the customer sees does not
// exist yet. Dispatch is on `key`, never on the visible label, so renaming one
// cannot silently unwire the handler.
type DocActionKey = 'view' | 'print' | 'save';

const DOC_ACTIONS: {
  key: DocActionKey;
  icon: typeof Eye;
  label: string;
  previewLabel: string;
}[] = [
  { key: 'view', icon: Eye, label: 'View Invoice PDF', previewLabel: 'View Preview PDF' },
  { key: 'print', icon: Printer, label: 'Print Invoice', previewLabel: 'Print Preview PDF' },
  { key: 'save', icon: Download, label: 'Save as PDF', previewLabel: 'Save Preview PDF' },
];

export const TicketDetail: React.FC = () => {
  const timeZone = useTenantTimeZone();
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const goBack = useBack('/tickets');
  const queryClient = useQueryClient();
  const { user, company, isFieldWorker } = useAuth();
  const currency = company?.country === 'CA' ? 'CAD' : 'USD';
  const isOfficeOrAdmin = canApproveTickets(user?.role);
  // A sales user reads the ticket and changes nothing (operator, 2026-10-11;
  // api#1032): the header keeps only the document menu, the resend buttons are
  // not drawn, and the reads refused to sales — field-ticket settings, the
  // signed-copy download — are neither fired nor offered. Every other role is
  // unchanged.
  const isSales = isSalesRole(user?.role);

  // UI state
  const [emailPromptOpen, setEmailPromptOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [docMenuOpen, setDocMenuOpen] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendRigEmail, setSendRigEmail] = useState('');
  const [sendCustomerEmail, setSendCustomerEmail] = useState('');
  const [sendRigName, setSendRigName] = useState('');
  const [saveAsRigEmail, setSaveAsRigEmail] = useState(true);
  /**
   * "Save as customer contact" (api#852). Off by default: a send is not a
   * decision to grow the client's address book. The name is optional for a
   * plain send (the API uses it for the SMS greeting) and required once the box
   * is ticked — the API refuses a blank one with a 400 before anything is sent,
   * so the modal says so first rather than letting the office meet the refusal.
   */
  const [sendContactName, setSendContactName] = useState('');
  const [saveAsContact, setSaveAsContact] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  /**
   * The customer's approval link, as the API returned it from THIS browser's
   * last send or resend: `data.reviewUrl` on success, and on the 502 an
   * email failure answers with. No ticket read carries it (api#1140 takes the
   * token off every read), so it is never loaded from the ticket, and a reload
   * clears it. It was read as `approvalUrl`, a key no route has ever sent, so
   * the Copy box never once appeared.
   */
  const [sendApprovalUrl, setSendApprovalUrl] = useState<string | null>(null);
  const [emailCorrectionOpen, setEmailCorrectionOpen] = useState(false);
  const [moveJobOpen, setMoveJobOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [moveResult, setMoveResult] = useState<MoveJobResult | null>(null);
  /**
   * A 409 `recipient_suppressed` from one of the three send doors. Held in
   * state rather than shown as a toast, for the reason the shared component
   * gives: the office needs the dated sentence in front of them while they fix
   * the address, and needs the `force: true` escape hatch. `door` records WHICH
   * send this was so the forced retry re-posts to the same one with the same
   * address — a force that went somewhere else would be this screen's original
   * bug wearing a new button.
   */
  const [suppressed, setSuppressed] = useState<
    { refusal: SuppressedRefusal; email?: string; door: 'send' | 'resend' } | null
  >(null);
  const docMenuRef = useRef<HTMLDivElement>(null);
  const docMenuPanelRef = useRef<HTMLDivElement>(null);
  // Where the portalled menu hangs: under the button, right edges aligned.
  const [docMenuAnchor, setDocMenuAnchor] = useState<{ top: number; right: number } | null>(null);

  const showToast = (text: string, type: 'success' | 'error' = 'success') => {
    setToastMsg({ text, type });
    setTimeout(() => setToastMsg(null), 4000);
  };

  // Close doc menu on outside click. The panel is portalled out of the button's
  // wrapper, so "inside" is either of the two.
  useEffect(() => {
    if (!docMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (docMenuRef.current?.contains(t) || docMenuPanelRef.current?.contains(t)) return;
      setDocMenuOpen(false);
    };
    // A fixed-position menu does not follow its button when <main> scrolls, so
    // it closes instead of drifting away from it.
    const close = () => setDocMenuOpen(false);
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', close, true);
    };
  }, [docMenuOpen]);

  const toggleDocMenu = () => {
    if (docMenuOpen) { setDocMenuOpen(false); return; }
    const rect = docMenuRef.current?.getBoundingClientRect();
    setDocMenuAnchor(rect ? { top: rect.bottom + 4, right: window.innerWidth - rect.right } : null);
    setDocMenuOpen(true);
  };

  // Fetch ticket
  const ticketQuery = useQuery({
    queryKey: ['ticket-detail', ticketId],
    queryFn: async () => {
      const res = await api.tickets.get(ticketId!);
      // `data: null` means the server has no ticket for this id. Map nothing —
      // mapTicket() on the envelope produces a ticket whose customer is
      // "Unknown", whose total is $0.00 and whose status is "draft", and every
      // downstream `if (ticket)` passes.
      const d = unwrap<any>(res.data);
      return d ? mapTicket(d) : null;
    },
    enabled: !!ticketId,
    // Retry anything worth retrying and NOT a refusal, and keep the error where
    // the screen can find it. Without this a 500 leaves the query `paused` with
    // `error: null`, `data: undefined` and `isLoading: false` — every branch
    // below would read that as "no ticket".
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const ticket = ticketQuery.data;
  const isLoading = ticketQuery.isLoading;
  const refetchTicket = ticketQuery.refetch;
  // Read off the QUERY, not off `error` alone: a query between retries carries
  // its failure in `failureReason` and nowhere else.
  const ticketFailure = queryLoadFailure(ticketQuery);

  // Pre-flight checks (api#908): the same cache entry the panel reads, so the
  // hint beside Office Approve costs no second request. SUGGEST NEVER BLOCKS —
  // the count is a pointer to the panel, never a reason to disable Approve.
  const billingFlagsQuery = useTicketBillingFlags(ticket?.id, ticket?.status, user?.role);
  const openPreflightChecks = openBillingFlagCount(billingFlagsQuery.data);

  // Email engagement polling
  const refetchForPolling = useCallback(async () => { await refetchTicket(); }, [refetchTicket]);
  useTicketEngagementPolling(ticket?.id ?? '', ticket?.customerEmailStatus, refetchForPolling);

  /**
   * THIS TENANT'S TICKET NUMBERING MODE, read only to decide whether the
   * move-job door exists at all.
   *
   * On `derived_from_job` the ticket number IS the parent job's — FT-4290-2 on
   * J-4290 — so a move would leave a number naming the wrong job, and the API
   * refuses for the whole tenant with `TICKET_NUMBER_DERIVED_FROM_JOB`. There
   * is nothing the office can do about that from here, so the button is not
   * rendered rather than rendered to produce a 409 (api#771).
   *
   * Same query key as the settings page, so the two share one cache entry.
   * The read is ungated; only the PATCH is admin-only.
   *
   * WHILE IT HAS NOT ARRIVED THE ANSWER IS `null`, NOT `sequential`, and the
   * button stays hidden. A default of "sequential" would flash a control that
   * the very next render may have to withdraw, and on a derived tenant that
   * flash is a door to a guaranteed refusal.
   */
  const { data: fieldTicketSettings } = useQuery({
    queryKey: ['settings', 'field-tickets'],
    queryFn: () =>
      apiClient
        .get<ApiResponse<'GET /api/v1/settings/field-tickets'>>('/settings/field-tickets')
        .then(r => unwrap<{ ticket_number_mode?: unknown }>(r.data)),
    enabled: !isSales,
  });
  const numberIsDerived = ticketNumberModeIsDerived(fieldTicketSettings?.ticket_number_mode);

  // --- Mutations ---
  // Every ticket mutation answers { success, data: <re-read ticket> }, and the
  // re-read returns null if the row vanished between the write and the read.
  // unwrap() surfaces that as null instead of handing back the envelope — a
  // null payload writes NOTHING to the cache, and the invalidate below refetches
  // the truth rather than pinning a phantom ticket into ['ticket-detail'].
  const submitMutation = useMutation({
    mutationFn: () => api.tickets.submit(ticketId!),
    onSuccess: (response) => {
      const d = unwrap<any>(response.data);
      if (d?.id) setQueryDataMapped(queryClient, ['ticket-detail', ticketId], mapTicket(d));
      invalidateTickets(queryClient);
      showToast('Submitted for approval');
    },
    onError: (err: any) => showToast(err?.response?.data?.error ?? 'Failed to submit', 'error'),
  });

  // The price-book gate's answer to the last approve (api#1042). Read from the
  // BODY: `priceBookWarning` is a sibling of `data`, so unwrap() drops it.
  const [priceBookGate, setPriceBookGate] = useState<
    { kind: 'warning' | 'refused'; lines: PriceBookUnlinkedLine[]; message?: string } | null
  >(null);

  const officeApproveMutation = useMutation({
    mutationFn: () => api.tickets.approve(ticketId!),
    onMutate: () => setPriceBookGate(null),
    onSuccess: (response) => {
      const warned = readPriceBookWarning(response.data);
      setPriceBookGate(warned ? { kind: 'warning', lines: warned } : null);
      const d = unwrap<any>(response.data);
      const updated = d?.id ? mapTicket(d) : null;
      if (updated) setQueryDataMapped(queryClient, ['ticket-detail', ticketId], updated);
      invalidateTickets(queryClient);
      if (updated?.needsCustomerEmail) {
        setEmailPromptOpen(true);
      } else {
        showToast('Ticket approved — customer notified');
      }
    },
    onError: (err: any) => {
      const refused = readPriceBookRefusal(err?.response?.data);
      if (refused) {
        // Its own notice, with the lines, where it stays until dismissed — a
        // toast would vanish before the office could act on the list.
        setPriceBookGate({ kind: 'refused', lines: refused.lines, message: refused.message });
        return;
      }
      showToast(err?.response?.data?.error ?? 'Failed to approve', 'error');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => api.tickets.reject(ticketId!, reason),
    onSuccess: (response) => {
      const d = unwrap<any>(response.data);
      if (d?.id) setQueryDataMapped(queryClient, ['ticket-detail', ticketId], mapTicket(d));
      invalidateTickets(queryClient);
      showToast('Ticket rejected');
      setShowRejectModal(false);
      setRejectReason('');
    },
    onError: (err: any) => showToast(err?.response?.data?.error ?? 'Failed to reject', 'error'),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => api.tickets.reactivate(ticketId!),
    onSuccess: (response) => {
      const d = unwrap<any>(response.data);
      if (d?.id) setQueryDataMapped(queryClient, ['ticket-detail', ticketId], mapTicket(d));
      invalidateTickets(queryClient);
      showToast('Ticket reactivated — you can now edit and resubmit');
    },
    onError: (err: any) => showToast(err?.response?.data?.error ?? 'Failed to reactivate', 'error'),
  });

  const archiveMutation = useMutation({
    mutationFn: () => api.tickets.archive(ticketId!),
    onSuccess: () => { invalidateTickets(queryClient); showToast('Ticket archived'); navigate('/tickets', { replace: true }); },
    onError: (err: any) => showToast(err?.response?.data?.error ?? 'Failed to archive', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.tickets.delete(ticketId!),
    onSuccess: () => { invalidateTickets(queryClient); showToast('Ticket deleted'); navigate('/tickets', { replace: true }); },
    onError: (err: any) => showToast(err?.response?.data?.error ?? 'Failed to delete', 'error'),
  });

  /**
   * The send-modal submit, lifted out of the button so the refusal notice's
   * "send anyway" can re-post the SAME address through the SAME door. A force
   * that re-opened the modal, or that went through a different door, would be
   * this screen's original defect wearing a new button.
   */
  /**
   * The client's saved contacts, for the picker and for the "already a
   * contact" test. "Save as customer contact" is offered only when the address
   * being sent to is NOT already one in the slice the API's save would match —
   * offering to save a contact that exists would be a box whose only possible
   * answer is 'existing'. While the list is loading the answer is unknown, so
   * the box waits; if the list FAILED it is offered, and the API's own match
   * still answers 'existing' rather than duplicating.
   */
  const sendContactsQuery = useSendModalContacts(ticket?.customerId, showSendModal);
  const sendAddress = sendRigEmail.trim() || sendCustomerEmail.trim();
  const offerSaveContact = !!sendAddress && (
    sendContactsQuery.error ? true
      : sendContactsQuery.data ? !isSavedContact(sendContactsQuery.data, sendAddress, { fromRigField: !!sendRigEmail.trim(), rigName: sendRigName })
      // No customer on the ticket: nothing to match against. The API decides,
      // and its 'no_customer' answer has its own toast.
      : !ticket?.customerId
  );
  const saveContactRequested = saveAsContact && offerSaveContact;

  const sendToCustomer = useCallback(async (opts: { email?: string; force?: boolean }) => {
    const rig = sendRigEmail.trim();
    const email = opts.email ?? (rig || sendCustomerEmail.trim());
    if (!email) return;
    const name = sendContactName.trim();
    // Only a fresh send from the modal can ask to save. A forced retry is the
    // same address again after a bounce — not a new choice — so it carries
    // neither save flag.
    // The blank-name case cannot reach here: the modal's Send is disabled for
    // it, and that is the one gate (a second copy here could never be tested).
    const wantsSave = !opts.force && saveContactRequested;
    setSendLoading(true);
    try {
      const res = await api.tickets.sendToCustomer(ticketId!, {
        email,
        // A forced retry re-sends to the refused address and nothing else. It
        // must not also stamp that address onto every job sharing the rig name
        // — the office is asserting the mailbox was repaired, not choosing a
        // new default for the rig.
        ...(opts.force ? { force: true } : {
          rigEmail: rig || undefined,
          saveRigEmail: saveAsRigEmail && !!rig,
          ...(name ? { contactName: name } : {}),
          ...(wantsSave ? { saveContact: true } : {}),
        }),
      });
      const d = res.data?.data ?? res.data ?? {};
      if (d.success === false) { showToast(`Email failed: ${d.error || 'Unknown error'}`, 'error'); if (d.reviewUrl) setSendApprovalUrl(d.reviewUrl); }
      else {
        // `contactSaved` rides on the ENVELOPE, beside `sentTo` — not inside
        // `data`, which is the enriched ticket. Every value is reported as a
        // send that went: the email has left by the time the API answers it.
        const saved = wantsSave ? sendContactSavedNote(res.data?.contactSaved) : '';
        showToast(`Email sent to ${email}${saved}`);
        if (wantsSave) queryClient.invalidateQueries({ queryKey: ['customer-contacts'] });
        if (d.reviewUrl) setSendApprovalUrl(d.reviewUrl); setShowSendModal(false); setSuppressed(null);
      }
      invalidateTickets(queryClient);
    } catch (err: any) {
      const refusal = suppressedRefusalOf(err);
      if (refusal) {
        // Nothing reached the provider and nothing was stamped. Close the modal
        // so the dated sentence and the escape hatch are what the office sees.
        setSuppressed({ refusal, email, door: 'send' });
        setShowSendModal(false);
        invalidateTickets(queryClient);
        return;
      }
      const errData = err?.response?.data;
      showToast(`Email failed: ${errData?.error || 'Failed to send'}`, 'error');
      // A failed email is a 502 whose `data` still carries the link: the one
      // moment the office most needs to copy it by hand.
      if (errData?.data?.reviewUrl) setSendApprovalUrl(errData.data.reviewUrl);
      invalidateTickets(queryClient);
    } finally { setSendLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, sendRigEmail, sendCustomerEmail, saveAsRigEmail, sendContactName, saveContactRequested, queryClient]);

  /**
   * A resend. `vars.email` is REQUIRED for anything calling itself a retry —
   * see api.tickets.resendCustomerEmail. Called with no vars it is a fresh
   * server-side lookup, which is legitimate only while the last delivery was
   * healthy; the header button that does that is hidden on a bounce.
   */
  const resendEmailMutation = useMutation({
    mutationFn: (vars?: { email?: string; force?: boolean }) =>
      api.tickets.resendCustomerEmail(ticketId!, vars),
    onSuccess: (response) => {
      setSuppressed(null);
      // `requiresEmail` and `sentTo` are SIBLINGS of `data` on the envelope, not
      // fields of the ticket. The old `?.data || ` collapsed both into one value
      // and then read ticket fields off it, so this branch only ever fired when
      // the unwrap had already fallen through. Read each from where it lives.
      const envelope = (response.data ?? {}) as { requiresEmail?: boolean; sentTo?: string };
      const d = unwrap<any>(response.data);
      if (envelope.requiresEmail) {
        setEmailPromptOpen(true);
      } else {
        if (d?.id) setQueryDataMapped(queryClient, ['ticket-detail', ticketId], mapTicket(d));
        if (d?.reviewUrl) setSendApprovalUrl(d.reviewUrl);
        invalidateTickets(queryClient);
        showToast(`Email resent to ${envelope.sentTo || 'customer'}`);
      }
    },
    onError: (err: any, vars) => {
      const refusal = suppressedRefusalOf(err);
      if (refusal) {
        // Refused before anything reached the provider. Show the sentence and
        // the escape hatch rather than a toast that vanishes.
        //
        // `canForce: false` when this call carried no address. The route's
        // `force` bypasses the bounce check on a BODY address; a body-less
        // resend is refused by the RESOLVER (#640) and forcing changes nothing,
        // so offering "send anyway" there is a button that cannot work — the
        // same class of dishonest control this whole change is about.
        setSuppressed({
          refusal: vars?.email ? refusal : { ...refusal, canForce: false },
          email: vars?.email,
          door: 'resend',
        });
        invalidateTickets(queryClient);
        return;
      }
      showToast(err?.response?.data?.error ?? 'Failed to resend email', 'error');
      // A 409 `recipient_suppressed` has already moved the ticket to
      // customer_email_status = 'bounced' server-side (the pre-send
      // guard in tickets.routes.ts). Without a refetch this screen keeps the
      // sky-blue "Awaiting customer approval" banner over a send that was
      // refused — the exact
      // false-healthy read the guard exists to end. The success path already
      // invalidates; the refusal path now does too.
      invalidateTickets(queryClient);
    },
  });

  // --- PDF helpers ---
  //
  // The API renders the customer-facing ticket PDF only for the statuses in
  // `TICKET_PDF_CUSTOMER_STATUSES`; everything else is a 409 unless the request
  // asks for `preview=1`. Compute the flag once, from the status this page is
  // currently showing, and derive both the request and the wording from it.
  const needsPreview = ticketPdfNeedsPreview(ticket?.status);

  // The customer's uploaded approval, or null. Read defensively: this is a raw
  // jsonb column off `t.*`, not a validated projection, so a row written by
  // anything other than the upload route could carry any shape at all.
  const stampedFile = ticket?.customerStampFile;
  const stampedFileName: string | null =
    stampedFile && typeof stampedFile === 'object' && typeof (stampedFile as any).filename === 'string'
      ? ((stampedFile as any).filename as string) || null
      : null;
  // Gate on the COLUMN, not on the name: a record with an unreadable filename is
  // still a file the office must be able to download, and hiding the button
  // would make a retrievable document unreachable from the only screen that
  // offers it.
  const hasStampedFile = !!stampedFile && typeof stampedFile === 'object';
  const stampedFileInfo = parseTicketStampFile(stampedFile);

  /**
   * "Attach customer-signed copy" (api#995): the statuses and roles the route
   * accepts, and only while the ticket holds neither a stamp nor a file — the
   * two 409s the office could not do anything about from here.
   */
  const canAttachSignedCopy =
    !!ticket &&
    canAttachCustomerSignedCopy(user?.role) &&
    ['office_approved', 'customer_approved', 'invoiced'].includes(ticket.status) &&
    !hasStampedFile &&
    !ticket.hasCustomerStamp;

  /**
   * "View Invoice" opens THE invoice. The ticket read carries `invoice_number`
   * and no invoice id (tickets has no invoice_id column; the link is the
   * invoice_tickets junction), and GET /invoices takes no number filter, so
   * the id is found by number in the company's casing invoice list. Exactly
   * one match opens it; anything else is said, never guessed at, and the list
   * is the fallback.
   */
  const [openingInvoice, setOpeningInvoice] = useState(false);
  const handleViewInvoice = async () => {
    const number = ticket?.invoiceNumber;
    if (!number) {
      showToast('This ticket does not name its invoice — showing the invoice list.', 'error');
      navigate('/invoices');
      return;
    }
    setOpeningInvoice(true);
    try {
      const rows = await queryClient.fetchQuery({
        queryKey: ['ticket-invoice-lookup', number],
        queryFn: async () => {
          const res = await api.invoices.list();
          const d = unwrap<any[]>(res.data);
          return Array.isArray(d) ? d : [];
        },
        staleTime: 30_000,
      });
      const hits = rows.filter((i: any) => (i.invoiceNumber ?? i.invoice_number) === number);
      if (hits.length === 1 && hits[0]?.id) {
        navigate(`/invoices/${hits[0].id}`);
      } else {
        showToast(
          hits.length === 0
            ? `Invoice ${number} was not found — showing the invoice list.`
            : `More than one invoice is numbered ${number} — showing the invoice list.`,
          'error',
        );
        navigate('/invoices');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, `Could not open invoice ${number}`), 'error');
    } finally {
      setOpeningInvoice(false);
    }
  };

  /**
   * Download the file the customer uploaded when they approved.
   *
   * THE FILENAME COMES OFF THE RESPONSE, NOT OUT OF THIS FILE. The server has
   * already sanitised the customer's own name and put it in `Content-Disposition`
   * (with a `filename*` copy for anything non-ASCII), so it is read back rather
   * than composed. `lib/ticket-pdf.ts` does the opposite — it overwrites the server's
   * name — and it is right to, because it is renaming a generated document to
   * mark a preview. This one is the customer's artifact and renaming it loses
   * the only label anybody has for it.
   *
   * A blob still needs `a.download` set to something: a blob URL carries no name,
   * so with the attribute absent the browser saves the object id. The recorded
   * filename on the ticket is the fallback when the header cannot be read.
   */
  const handleDownloadStampedFile = async () => {
    try {
      const res = await api.tickets.getStampedFile(ticketId!);
      const name =
        filenameFromContentDisposition(res.headers?.['content-disposition']) ??
        stampedFileName ??
        'stamped-ticket';
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // Blob-aware: the error body of a `responseType: 'blob'` request is itself
      // a Blob, and the plain reader would show none of it.
      showToast(await getBlobApiErrorMessage(err, 'Failed to download the signed ticket'), 'error');
    }
  };

  /**
   * THE TAB IS CLAIMED ON THE CLICK, BEFORE THE AWAIT — NOT AFTER IT.
   *
   * This used to `await` the PDF and then call `window.open`, which is past
   * Chrome's five-second transient-activation budget once the render (~2.6s
   * measured) and the user's network are added. `window.open` then returns
   * `null` and opens nothing WITHOUT throwing, so the catch below never ran and
   * the button silently did nothing (fe#763). See `lib/blob-tab.ts` for the
   * measurement and the reasoning; the ordering is the fix, not the reporting.
   *
   * PRINT ON A PHONE OR TABLET GOES THROUGH THE SAME TAB, for the same
   * reason and one more: Android and iOS have no inline PDF viewer, so a hidden
   * frame there prints the page around it or nothing. The browser's own viewer
   * in a tab is what prints there. Desktop Print uses an unseen frame — see
   * `printBlobInFrame` for why it waits for load and is never removed on a timer.
   */
  const handleDocAction = (key: DocActionKey) => {
    setDocMenuOpen(false);
    // Synchronous up to the tab claim — see lib/ticket-pdf.ts and lib/blob-tab.ts.
    void runTicketPdfAction(
      key,
      { id: ticketId!, status: ticket?.status, ticketNumber: ticket?.ticketNumber },
      { onError: (m) => showToast(m, 'error'), onNotice: (m) => showToast(m) },
    );
  };

  // The print frame outlives the click on purpose (its dialog lives in it);
  // leaving the screen is when it goes.
  useEffect(() => () => disposePrintFrame(), []);

  // --- The load did not arrive, the load found nothing, and the load is
  //     still running are THREE states, and the middle one is the one this
  //     screen used to swallow. ---
  //
  // `{ data, isLoading }` has two branches by construction: a 403, a 500 and a
  // dead zone all arrive as `data: undefined`, which `!ticket` then reported as
  // "may have been deleted, or you may not have access to it" — a sentence that
  // names deletion first and is wrong for every one of them. A refusal is an
  // answer; an unreachable server is not; neither is a missing row.
  if (ticketFailure) {
    return (
      <div data-testid="ticket-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <TicketBand title={ticketId ?? 'Field ticket'} onBack={goBack} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8">
              <LoadFailureNotice
                error={queryFailureError(ticketQuery)}
                /* A PLURAL NOUN PHRASE, because that is what the helper's
                   template needs: it renders "not a statement about your
                   {subject}", and "this ticket" made that read "about your
                   this ticket". Caught by reading the rendered sentence in a
                   browser, not by any test — the string typechecks either
                   way. */
                subject="ticket details"
                roleLabel={user?.role ? getRoleLabel(user.role) : undefined}
                allowed="admins and office staff"
              />
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div data-testid="ticket-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <TicketBand title="Loading…" onBack={goBack} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto flex max-w-[1920px] items-center justify-center gap-2 py-20 text-fieldiq-text">
            <Loader2 size={20} className="animate-spin" /> Loading ticket…
          </div>
        </section>
      </div>
    );
  }

  // The load finished, the server answered, and it had no ticket for this id
  // (`data: null`, or a 404 react-query surfaced). A fact about the row, not
  // about the connection — which is why it no longer shares a branch with the
  // failure above.
  if (!ticket) {
    return (
      <div data-testid="ticket-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <TicketBand title="Ticket not found" onBack={goBack} />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-8 text-center">
              <AlertTriangle size={32} className="mx-auto mb-3 text-fieldiq-muted" aria-hidden="true" />
              <p className="text-sm font-semibold text-white">There is no ticket with this id</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-fieldiq-muted">
                It may have been deleted, or the link may be wrong.
              </p>
              <button
                onClick={() => navigate('/tickets')}
                className="fi-action fi-action--standard fi-action--primary mt-4 inline-flex"
              >
                View all tickets
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const skin = statusSkin(ticket.status);
  const StatusIcon = skin.icon;
  const showEmailPill = ticket.customerEmailStatus && ticket.customerEmailStatus !== 'not_sent' &&
    ((ticket.status === 'office_approved' && !ticket.customerApprovedAt && !ticket.customerRejectionReason) ||
     ['bounced', 'failed', 'complained'].includes(ticket.customerEmailStatus));

  // Why Edit / Delete are locked, or null when they are not. The API owns both
  // rules and refuses us either way; these render the refusal BEFORE the click
  // instead of as a 409 after it. The two lists differ on purpose — an
  // office-approved ticket can be edited (it reverts) but not deleted.
  /**
   * WHETHER THE MOVE-JOB DOOR IS OFFERED. Four independent conditions, every
   * one of which the API also enforces — this only decides whether to show a
   * control, never whether the move is allowed.
   *
   *  - role: EXACTLY `admin` / `office_staff`, the API's own gate. Not
   *    `canApproveTickets`, which also admits supervisor and owner and would
   *    hand them a 403.
   *  - status: customer_approved and invoiced are frozen (guard 1). The
   *    refusal would be correct; the button should not have been offered.
   *  - numbering: `derived_from_job` refuses for the whole tenant (guard 6).
   *    `null` means the setting has not arrived — hide, do not assume.
   */
  const canMoveJob =
    canMoveTicketJob(user?.role) && !moveJobIsFrozen(ticket.status) && numberIsDerived === false;

  const statusLabel = officeTicketStatusLabel(ticket.status);
  const editLockReason = ticketEditIsFrozen(ticket.status)
    ? `${statusLabel} tickets can no longer be edited — they are a record of what was agreed.`
    : null;
  const deleteLockReason = ticketDeleteIsBlocked(ticket.status)
    ? `${statusLabel} tickets cannot be deleted.${
        ticketEditIsFrozen(ticket.status) ? '' : ' Edit it instead — that returns it for re-approval.'
      }`
    : null;

  // --- Header actions (title row, right side) ---
  /**
   * EXACTLY ONE AMBER ACTION ON THE BAND, and it is the one that moves the
   * ticket forward.
   *
   * The buttons and their handlers are unchanged; this only decides which of
   * them wears the primary skin. Before the re-skin every forward action had
   * its own hardcoded fill — green for Office Approve, blue for Send, a second
   * green for Submit as Invoice — so an office-approved, unsent ticket showed
   * a blue primary and a green primary side by side with nothing saying which
   * came first. The order below is the ticket's own order.
   */
  const primaryAction: 'submit' | 'approve' | 'send' | 'invoice' | 'reactivate' | null =
    ticket.status === 'draft' || ticket.status === 'active' ? 'submit'
    : ticket.status === 'field_approved' && isOfficeOrAdmin ? 'approve'
    : ticket.status === 'office_approved' && isOfficeOrAdmin ? 'send'
    : ticketIsInvoiceable(ticket.status) && isOfficeOrAdmin ? 'invoice'
    : ticket.status === 'rejected' || (ticket.status as string) === 'customer_rejected' ? 'reactivate'
    : null;

  const ACTION_PRIMARY = 'fi-action fi-action--standard fi-action--primary';
  /* A secondary action ON THE BAND takes a white/25 border and NO colour class:
     it inherits the band's near-white ink, which a `text-white` utility would
     lose the moment the app switched to light. */
  const ACTION_SECONDARY =
    'fi-action fi-action--standard border border-white/25';
  /* Icon-only controls, same rule, and the hover is spelled as an OPACITY.
     The bare white text utility is remapped to the heading colour in light
     mode and its hover variant deliberately is not, so pairing the two puts
     literal #fff on a pale surface the moment a pointer lands on it. The
     fractional spellings are exempt from that remap, which is precisely what
     the band wants: constant near-white in both halves. The body below must
     not copy this — down there the remap is the correct behaviour. */
  const ACTION_ICON =
    'inline-flex items-center justify-center rounded-fi-control border border-white/25 p-2 text-white/80 transition hover:bg-white/10 hover:text-white/100 disabled:opacity-40 disabled:cursor-not-allowed';
  const ACTION_ICON_DANGER =
    'inline-flex items-center justify-center rounded-fi-control border border-fi-signal-red/60 p-2 text-white/80 transition hover:bg-fi-signal-red/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';

  const headerActions = (
    <div className="flex items-center gap-2">
      {!isSales && (<>
      {/* draft/active → Submit for Approval */}
      {(ticket.status === 'draft' || ticket.status === 'active') && (
        <button
          onClick={() => submitMutation.mutate()}
          disabled={submitMutation.isPending}
          className={primaryAction === 'submit' ? ACTION_PRIMARY : ACTION_SECONDARY}
        >
          {submitMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Submit &amp; Field Approve
        </button>
      )}

      {/* field_approved → open pre-flight checks, a pointer to the panel below.
          Spelled in a constant white opacity because it sits on the band. */}
      {ticket.status === 'field_approved' && isOfficeOrAdmin && openPreflightChecks > 0 && (
        <button
          type="button"
          onClick={() =>
            document.getElementById(BILLING_FLAGS_PANEL_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          className="whitespace-nowrap text-xs font-bold text-white/80 underline decoration-white/40 underline-offset-2 hover:text-white/100"
        >
          {openPreflightChecks} open pre-flight {openPreflightChecks === 1 ? 'check' : 'checks'}
        </button>
      )}

      {/* field_approved → Office Approve */}
      {ticket.status === 'field_approved' && isOfficeOrAdmin && (
        <button
          onClick={() => officeApproveMutation.mutate()}
          disabled={officeApproveMutation.isPending}
          className={primaryAction === 'approve' ? ACTION_PRIMARY : ACTION_SECONDARY}
        >
          {officeApproveMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
          Office Approve
        </button>
      )}

      {/* submitted/field_approved → Reject */}
      {((ticket.status === 'submitted' && isFieldWorker) || (ticket.status === 'field_approved' && isOfficeOrAdmin)) && (
        <Tooltip content="Reject">
          <button
            onClick={() => setShowRejectModal(true)}
            className={ACTION_ICON_DANGER}
            aria-label="Reject"
          >
            <XCircle size={16} />
          </button>
        </Tooltip>
      )}

      {/* office_approved → the address modal. NOT gated on `!sentToCustomerAt`.
          It used to be, and that made the ONLY free-text recipient field on
          this screen vanish permanently the moment a send succeeded —
          including a send that succeeded and then hard-bounced three seconds
          later, which is every bounce, because a bounce is asynchronous and
          arrives after a 200. What remained was an address-less Resend.
          `POST /send-to-customer` has always accepted `office_approved`
          regardless of whether a send already happened; only the button was
          conditional. */}
      {ticket.status === 'office_approved' && isOfficeOrAdmin && (
        <button
          onClick={async () => {
            try {
              const r = await api.tickets.getCustomerContacts(ticketId!);
              const d = r.data.data ?? r.data ?? {};
              setSendRigEmail(d.rigEmail || ticket.rigEmail || '');
              setSendCustomerEmail(d.customerEmail || '');
              setSendRigName(d.rigName || ticket.rigName || '');
              setSaveAsRigEmail(true);
            } catch {
              setSendRigEmail(ticket.rigEmail || '');
              setSendRigName(ticket.rigName || '');
            }
            setSendContactName('');
            setSaveAsContact(false);
            setShowSendModal(true);
          }}
          /* Already sent: the forward step has been taken, so this drops
             to a secondary and the amber goes nowhere else on the band. */
          className={primaryAction === 'send' && !ticket.sentToCustomerAt ? ACTION_PRIMARY : ACTION_SECONDARY}
        >
          <Send size={14} /> {ticket.sentToCustomerAt ? 'Send to another address' : 'Send to Customer'}
        </button>
      )}

      {/* office_approved + already sent → Resend.
          NOT while the last delivery bounced or failed. This is the same
          address-less mutation the banner's "Retry Same Address" called, and on
          a bounced ticket it sits one row above a red banner telling the office
          to correct the address — two controls, opposite advice, and the blind
          one is the easier click. (#602 added this guard; the paragraph below
          is why it stays now that a real retry exists beneath it.)

          It CANNOT reach the address that bounced, and that has been true in
          two different ways. Before #640 the resolver walked past a suppressed
          address and promoted the next source, so this button sent the ticket
          to a different person — TNT FT-04444, 2026-09-09: it fired twice and
          delivered to the consultant who had just been replaced, and he opened
          it. Since #640 the resolver refuses instead, so the button now returns
          a 409 it cannot escape: `force` bypasses the check on a BODY address,
          and this call has no body. Either way there is nothing here for the
          office. The banner below is where a retry belongs, because that is the
          only place the recipient is named.

          On a healthy send it is a legitimate reminder to whoever the ticket
          already reached, and its toast names them. */}
      {ticket.status === 'office_approved' && ticket.sentToCustomerAt && isOfficeOrAdmin
        && !['bounced', 'failed'].includes(ticket.customerEmailStatus || '') && (
        <button
          onClick={() => resendEmailMutation.mutate(undefined)}
          disabled={resendEmailMutation.isPending}
          className={ACTION_SECONDARY}
        >
          {resendEmailMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Resend Email
        </button>
      )}

      {/* office_approved or customer_approved → Submit as Invoice. The API has
          always accepted both; this button alone read customer_approved, which
          is how a customer who never clicks the link held a ticket unbillable. */}
      {ticketIsInvoiceable(ticket.status) && isOfficeOrAdmin && (
        <button
          onClick={() => navigate('/invoices/generate', { state: { ticketIds: [ticket.id] } })}
          className={primaryAction === 'invoice' ? ACTION_PRIMARY : ACTION_SECONDARY}
        >
          <FileText size={14} /> Submit as Invoice
        </button>
      )}

      {/* invoiced → View Invoice */}
      {ticket.status === 'invoiced' && !isFieldWorker && (
        <button
          onClick={handleViewInvoice}
          disabled={openingInvoice}
          className={ACTION_SECONDARY}
          data-testid="view-invoice"
        >
          {openingInvoice ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          {ticket.invoiceNumber ? `View Invoice ${ticket.invoiceNumber}` : 'View Invoice'}
        </button>
      )}

      {/* A copy the customer signed outside FieldIQ (api#995). Admin and
          office staff only (strict on the route), and absent once the ticket
          holds a stamp or a file. */}
      {canAttachSignedCopy && (
        <button
          onClick={() => setAttachOpen(true)}
          className={ACTION_SECONDARY}
          data-testid="attach-signed-copy"
        >
          <Paperclip size={14} /> Attach customer-signed copy
        </button>
      )}

      {/* rejected/customer_rejected → Reactivate & Edit */}
      {(ticket.status === 'rejected' || (ticket.status as string) === 'customer_rejected') && (
        <button
          onClick={() => reactivateMutation.mutate()}
          disabled={reactivateMutation.isPending}
          className={primaryAction === 'reactivate' ? ACTION_PRIMARY : ACTION_SECONDARY}
        >
          {reactivateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <ArchiveRestore size={14} />}
          Reactivate
        </button>
      )}

      {/* View Pay — approved+ statuses */}
      {(['field_approved', 'office_approved', 'customer_approved', 'approved', 'invoiced'] as string[]).includes(ticket.status) && (
        <Tooltip content="View Pay">
          <button
            onClick={() => navigate(`/tickets/${ticket.id}/pay`)}
            className={ACTION_ICON}
            aria-label="View Pay"
          >
            <DollarSign size={16} />
          </button>
        </Tooltip>
      )}

      {/* Move to another job. Hidden, not disabled: the three things that
          withdraw it (role, a frozen status, derived numbering) are not states
          the office can clear from this screen, so a disabled button would be a
          permanent locked control with nothing to do about it. Edit below is
          disabled-with-a-reason for the opposite reason — the reason is
          actionable. */}
      {canMoveJob && (
        <Tooltip content="Move to another job">
          <button
            onClick={() => setMoveJobOpen(true)}
            className={ACTION_ICON}
            aria-label="Move to another job"
            data-testid="move-job-button"
          >
            <ArrowLeftRight size={16} />
          </button>
        </Tooltip>
      )}

      {/* Edit — disabled, never hidden, once the API freezes the ticket. The
          user needs to see the button exists and is locked, and why. */}
      <Tooltip content={editLockReason ?? 'Edit ticket'}>
        <button
          onClick={() => navigate(`/tickets/${ticketId}/edit`)}
          disabled={!!editLockReason}
          aria-disabled={!!editLockReason}
          className={ACTION_ICON}
          aria-label={editLockReason ?? 'Edit ticket'}
        >
          <Pencil size={16} />
        </button>
      </Tooltip>

      {/* Archive */}
      <Tooltip content="Archive ticket">
        <button
          onClick={() => { if (window.confirm('Archive this ticket?')) archiveMutation.mutate(); }}
          disabled={archiveMutation.isPending}
          className={ACTION_ICON}
          aria-label="Archive ticket"
        >
          {archiveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />}
        </button>
      </Tooltip>

      {/* Delete — blocked on a WIDER set of statuses than Edit. Deleting is not
          editing: an office-approved ticket may be corrected, not made to
          disappear. Same class of bug as Edit was — this 409'd on every click. */}
      <Tooltip content={deleteLockReason ?? 'Delete ticket'}>
        <button
          onClick={() => { if (window.confirm('Permanently delete this ticket? This cannot be undone.')) deleteMutation.mutate(); }}
          disabled={deleteMutation.isPending || !!deleteLockReason}
          aria-disabled={!!deleteLockReason}
          className={ACTION_ICON_DANGER}
          aria-label={deleteLockReason ?? 'Delete ticket'}
        >
          {deleteMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
        </button>
      </Tooltip>

      {/* The customer's own signed copy. A separate control from the document
          menu on purpose: everything in that menu is a document WE render from
          the ticket, and this is the one the customer sent us. It appears only
          when the ticket actually carries one — most do not, because the usual
          path is the stamp signed on the review page. */}
      </>)}
      {hasStampedFile && !isSales && (
        <Tooltip content={stampedFileName ? `Download signed ticket — ${stampedFileName}` : 'Download signed ticket'}>
          <button
            onClick={handleDownloadStampedFile}
            className={ACTION_ICON}
            aria-label="Download signed ticket"
            data-testid="download-stamped-file"
          >
            <Paperclip size={16} />
          </button>
        </Tooltip>
      )}

      {/* Document menu (includes Download PDF) */}
      <div className="relative" ref={docMenuRef}>
        <Tooltip content="Documents">
          <button onClick={toggleDocMenu} className={ACTION_ICON} aria-label="Documents" aria-expanded={docMenuOpen}>
            <FileText size={16} />
          </button>
        </Tooltip>
        {/* PORTALLED TO <body>, NOT HUNG OFF THE BUTTON. The button is on the
            page header band, which clips its contents (overflow: hidden keeps
            the hero circle inside the rounded panel) — so a menu hung from it
            is cut off at the band's bottom edge, and the preview notice makes
            it tall enough to lose "Save Preview PDF". Solid card surface in
            both themes; the band's own ink rules cannot reach it out here. */}
        {docMenuOpen && createPortal(
          <div
            ref={docMenuPanelRef}
            data-testid="ticket-doc-menu"
            style={docMenuAnchor ? { top: docMenuAnchor.top, right: docMenuAnchor.right } : undefined}
            className="fixed z-50 w-64 animate-fade-in rounded-fi-panel border border-fieldiq-border bg-fieldiq-card py-1.5 shadow-2xl"
          >
            {/* The watermark is on the document; the decision is made here. Say
                which document these three buttons produce *before* the click,
                and why the customer's copy is missing. */}
            {needsPreview && (
              <p className="border-b border-fieldiq-border px-4 py-2 text-[11px] leading-snug text-fieldiq-accent-signal">
                Watermarked preview — not the customer's document. The customer copy is
                created when the ticket is office-approved, and only then can it be emailed.
              </p>
            )}
            {DOC_ACTIONS.map((item) => (
              <button key={item.key} onClick={() => handleDocAction(item.key)} className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-fieldiq-text transition-colors hover:bg-fieldiq-dark hover:text-fieldiq-accent">
                <item.icon size={15} /> {needsPreview ? item.previewLabel : item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="ticket-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <TicketBand
        title={ticket.ticketNumber || ticketId || 'Field ticket'}
        onBack={goBack}
        actions={headerActions}
        /* FOUR FIGURES THE TICKET ITSELF STATES, none of them recomputed and
           none of them a second answer to a question the API already answers.
           Total and Hours come off the row as mapped (both already `\u2014`
           when absent); Lines and Crew are lengths of arrays this screen has
           in hand and renders below, so they cannot disagree with what is on
           the page.

           THE THIRD LABEL IS FIVE CHARACTERS ON PURPOSE. The hero circle's
           edge crosses the third stat column between 1024 and 1048 wide, and
           the labels swept there run to about 70px. "Line Items" would have
           needed the sweep re-run; "Lines" with the sub-line doing the
           explaining does not. All four were re-measured anyway — this is the
           first DETAIL page to carry the band, so no previous sweep covers
           this copy. */
        stats={[
          {
            icon: <DollarSign className="size-4" aria-hidden="true" />,
            label: 'Total',
            sub: 'As invoiced',
            tone: 'success',
            value: fmtCurrency(ticket.total, currency),
          },
          {
            icon: <Clock className="size-4" aria-hidden="true" />,
            label: 'Hours',
            sub: 'On this ticket',
            tone: 'accent',
            /* The bare figure — the label already says Hours, and repeating
               the unit here would put a second "9.50 hrs" on a page that
               already renders one in the ticket record below. `hours` is
               already `null` when the times contradict each other, and a
               contradiction is not zero hours, so it reads as an em dash up
               here and the record below is where the contradiction is named. */
            value: ticket.hours ?? '\u2014',
          },
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Lines',
            sub: 'Billed items',
            tone: 'info',
            value: displayValue(ticket.lineItems.length),
          },
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: 'Crew',
            sub: 'On the job',
            tone: 'neutral',
            value: displayValue(ticket.crew.length),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
        {/* Toast */}
        {toastMsg && (
          <div
            role="status"
            className={`flex items-center gap-2 rounded-fi-panel border p-3 text-sm text-white ${
              toastMsg.type === 'success'
                ? 'border-fieldiq-success/40 bg-fieldiq-success/10'
                : 'border-fieldiq-danger/40 bg-fieldiq-danger/10'
            }`}
          >
            {toastMsg.type === 'success' ? <CheckCircle size={16} className="text-fieldiq-text" /> : <XCircle size={16} className="text-fieldiq-text" />}
            {toastMsg.text}
          </div>
        )}

        {/* Billing pre-flight checks — in the BODY, never on the band. Renders
            nothing outside suggest mode, for a role it is not for, or on a
            ticket the checks do not apply to. */}
        <BillingFlagsPanel ticketId={ticket.id} ticketStatus={ticket.status} />

        {priceBookGate && (
          <PriceBookGateNotice
            kind={priceBookGate.kind}
            lines={priceBookGate.lines}
            message={priceBookGate.message}
            onDismiss={() => setPriceBookGate(null)}
          />
        )}

        {/* What the last move did. Above everything, because two of its four
            outcomes are jobs for the reader — read the customer notes,
            re-approve and re-send — and the ticket below now shows the NEW job
            with nothing on it saying it changed. */}
        {moveResult && (
          <MoveTicketJobOutcome
            result={moveResult}
            ticketId={ticket.id}
            onDismiss={() => setMoveResult(null)}
          />
        )}

        {/* Status + Date + Engagement row */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              data-testid="ticket-status-badge"
              className={`inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
            >
              <StatusIcon size={10} aria-hidden="true" /> {skin.label}
            </span>
            {/* The approval marks are neutral-outlined, the way the register's
                are (fe#730): the badge to their left already carries the hue,
                and a second coloured chip saying the same thing in a different
                colour is what put green "Office ✓" next to a blue "Office
                Approved". The tick and the cross carry the rest. */}
            {(ticket.status as string) === 'customer_rejected' && <span className={APPROVAL_MARK}>Customer ✗</span>}
            {ticket.status === 'field_approved' && <span className={APPROVAL_MARK}>Field ✓</span>}
            {(ticket.status === 'office_approved' || ticket.status === 'approved') && <span className={APPROVAL_MARK}>Office ✓</span>}
            {/* The customer's tick reads the customer's stamp, not the status: an
                invoiced ticket may have been billed straight from office_approved,
                and `invoiced` used to render "Customer ✓" for one nobody signed. */}
            {(ticket.status === 'customer_approved' || !!ticket.customerApprovedAt) && <span className={APPROVAL_MARK}>Customer ✓</span>}
            {showEmailPill && (
              <EmailEngagementPill
                status={ticket.customerEmailStatus}
                errorMessage={ticket.customerEmailLastError}
                lastAttemptAt={ticket.customerEmailLastAttemptAt}
                deliveredAt={ticket.customerEmailDeliveredAt}
                openCount={ticket.customerEmailOpenCount}
                lastOpenedAt={ticket.customerEmailLastOpenedAt}
                clickCount={ticket.customerEmailClickCount}
                lastClickedAt={ticket.customerEmailLastClickedAt}
                complainedAt={ticket.customerEmailComplainedAt}
              />
            )}
          </div>
          <span className="text-sm text-fieldiq-muted">{fmtDate(ticket.date)}</span>
        </div>

        {/* Contextual status banners */}
        {ticket.status === 'office_approved' && !ticket.sentToCustomerAt && (
          <div className="flex items-center gap-2 rounded-fi-panel border border-fi-amber/40 bg-fi-amber/10 p-3">
            <Mail size={14} className="flex-shrink-0 text-fieldiq-text" aria-hidden="true" />
            <p className="text-sm font-medium text-white">Office approved — not yet sent to customer</p>
          </div>
        )}
        {ticket.status === 'office_approved' && ticket.sentToCustomerAt && !['bounced', 'failed'].includes(ticket.customerEmailStatus || '') && (
          <div className="flex items-center gap-2 rounded-fi-panel border border-fi-blue/40 bg-fi-blue/10 p-3">
            <Clock size={14} className="flex-shrink-0 text-fieldiq-text" aria-hidden="true" />
            <p className="text-sm font-medium text-white">Awaiting customer approval — sent {fmtDateTime(ticket.sentToCustomerAt, timeZone)}</p>
          </div>
        )}
        {/* THE APPROVAL BANNER, at customer_approved AND invoiced. It used to
            render at customer_approved only, so invoicing a ticket took the
            approval and the signed copy off the screen at the moment the
            office most needs to show them.

            On an invoiced ticket it renders only when there is something to
            show: a ticket billed straight from office_approved has no customer
            approval, and a green "Customer approved" over it would be a claim
            nobody made. A file with no recorded approval (attached after
            invoicing) reads as the file, not as an approval. */}
        {(((ticket.status as string) === 'customer_approved') ||
          (ticket.status === 'invoiced' && (!!ticket.customerApprovedAt || !!ticket.customerApprovedByName || hasStampedFile))) && (
          <div className="flex items-start gap-2 rounded-fi-panel border border-fieldiq-success/40 bg-fieldiq-success/10 p-3" data-testid="customer-approval-banner">
            <CheckCircle size={14} className="mt-0.5 flex-shrink-0 text-fieldiq-text" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-white">
                {ticket.customerApprovedAt || ticket.customerApprovedByName || (ticket.status as string) === 'customer_approved'
                  ? <>Customer approved{ticket.customerApprovedByName ? ` by ${ticket.customerApprovedByName}` : ''}{ticket.customerApprovedAt ? ` on ${fmtInstantDay(ticket.customerApprovedAt, timeZone)}` : ''}</>
                  : 'Customer-signed copy on file'}
              </p>
              {/* Which door the file came through, because the two are
                  different claims: the customer sent it, or the office put it
                  here on their behalf. Without the download the only sign a
                  signed document exists is a paperclip icon in the header. */}
              {hasStampedFile && (
                <p className="mt-0.5 text-xs text-fieldiq-text" data-testid="stamped-file-source">
                  {stampedFileInfo?.source === 'office'
                    ? `Attached by office (${stampedFileInfo.attachedByName ?? '—'}, ${fmtInstantDay(stampedFileInfo.attachedAt, timeZone)})`
                    : stampedFileInfo?.source === 'customer'
                      ? 'Uploaded by customer'
                      : 'Signed copy on file'}
                  {!isSales && ' · '}
                  {!isSales && <button
                    onClick={handleDownloadStampedFile}
                    className="text-left underline underline-offset-2 transition-colors hover:text-fieldiq-accent"
                    data-testid="stamped-file-banner-link"
                  >
                    Download{stampedFileName ? ` ${stampedFileName}` : ' signed copy'}
                  </button>}
                </p>
              )}
            </div>
          </div>
        )}
        {/* An OFFICE rejection, and it reads the office's own column. This used
            to render `customerRejectionReason` — the customer's words, under a
            heading claiming to explain why the office sent it back. The two
            columns are written by different routes and neither clears the
            other, so on the one ticket that can hold both this attributed the
            customer's reason to the office, and on every other office-rejected
            ticket the column was null and the reason simply never appeared.
            The customer's rejection has its own banner below. */}
        {ticket.status === 'rejected' && ticket.rejectionReason && (
          <div className="rounded-fi-panel border border-fieldiq-danger/40 bg-fieldiq-danger/10 p-3">
            <p className="mb-1 text-xs font-medium text-fieldiq-text">Rejection Reason</p>
            <p className="text-sm text-white">{ticket.rejectionReason}</p>
          </div>
        )}

        {/* Customer rejection — prominent banner with reason + path forward */}
        {(ticket.status as string) === 'customer_rejected' && (
          <div className="space-y-3 rounded-fi-panel border border-fieldiq-danger/40 bg-fieldiq-danger/10 p-4">
            <div className="flex items-start gap-2.5">
              <XCircle size={18} className="mt-0.5 flex-shrink-0 text-fieldiq-text" aria-hidden="true" />
              <div>
                <p className="text-sm font-bold text-white">
                  Rejected by customer
                  {ticket.customerRejectedByName ? ` — ${ticket.customerRejectedByName}` : ''}
                  {ticket.customerRejectedAt ? ` on ${fmtDateTime(ticket.customerRejectedAt, timeZone)}` : ''}
                </p>
                {ticket.customerRejectionReason && (
                  <p className="text-sm text-white mt-1">
                    <span className="text-fieldiq-muted">Reason:</span> “{ticket.customerRejectionReason}”
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-2.5 border-t border-fieldiq-danger/30 pt-3">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-fieldiq-text" aria-hidden="true" />
              <div className="text-xs text-fieldiq-text space-y-1">
                <p className="text-white font-medium">What happens next</p>
                {/* This used to read "Editing this ticket reverts it to Draft and
                    restarts the approval flow", which described the Reactivate
                    button, not Edit. Editing a customer-rejected ticket saves in
                    place and leaves the status alone — only an office-approved
                    ticket is reverted by an edit. Naming the wrong control is how
                    someone edits, sees the status unchanged, and reports it as a
                    save that did not take. */}
                <p>
                  <span className="text-white font-medium">Reactivate</span> returns this ticket to{' '}
                  <span className="text-white font-medium">Draft</span> and restarts the approval flow
                  (field → office → customer). The customer gets a fresh review link once it is approved
                  and sent again.
                </p>
                <p>
                  Editing it without reactivating saves your changes and leaves it{' '}
                  <span className="text-white font-medium">Customer Rejected</span>.
                </p>
                <p>
                  Use <span className="text-white font-medium">Customer Notes</span> to explain the revision so the
                  customer understands what changed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* The 409 from a send door. Same component and same `force: true`
            contract the invoice and quote screens have had since the guard
            landed; the ticket doors accepted `force` the whole time and nothing
            on this screen ever sent it, so a suppressed ticket recipient could
            not be retried by anyone in any role. */}
        {suppressed && (
          <SuppressedRecipientNotice
            refusal={suppressed.refusal}
            documentLabel="ticket"
            busy={resendEmailMutation.isPending || sendLoading}
            onForce={() => {
              if (suppressed.door === 'resend') {
                resendEmailMutation.mutate({ email: suppressed.email, force: true });
              } else {
                void sendToCustomer({ email: suppressed.email, force: true });
              }
            }}
            onDismiss={() => setSuppressed(null)}
          />
        )}

        {/* Email failure banner with address correction.

            ONE source for the recipient: `customerEmailSentTo`, the address the
            send door recorded before it called the provider (API 0391). It used
            to be read as `customerEmailSentTo || rigEmail || customerContactEmail`
            and the first two resolved to nothing — no such column, no such
            payload key — so every bounce banner named `customerContactEmail`,
            which the API fills from `customers.main_contact_email`. TNT
            FT-04444: the send that bounced went to murry.cochrane@cenovus.com
            and this banner read "Delivery bounced to info@cenovus.com", a live
            switchboard that had never been sent anything.

            Absence is now stated, not substituted. A `||` chain onto another
            address is the silent-blank-field family with the worst possible
            filler: not a zero, a different person's mailbox. */}
        {ticket.customerEmailStatus && ['failed', 'bounced'].includes(ticket.customerEmailStatus) && (
          // role="alert": this banner is the only place the office is told a
          // send did not arrive, and it names the remedy. It also gives the
          // region a handle a test can scope to, so "the only control here is
          // the one that takes an address" is assertable without reaching into
          // the DOM by class name.
          <div role="alert" className="space-y-3 rounded-fi-panel border border-fieldiq-danger/40 bg-fieldiq-danger/10 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-fieldiq-text" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-white">
                  Delivery {ticket.customerEmailStatus === 'bounced' ? 'bounced' : 'failed'}
                  {ticket.customerEmailSentTo
                    ? <> to <span className="text-white font-semibold">{ticket.customerEmailSentTo}</span></>
                    : <> — <span className="text-white font-semibold">recipient not recorded</span></>}
                </p>
                <p className="text-xs text-fieldiq-muted mt-0.5">
                  {ticket.customerEmailLastError || 'The address may be incorrect or the mailbox may be unavailable.'}
                </p>
                {!ticket.customerEmailSentTo && (
                  <p className="text-xs text-fieldiq-muted mt-0.5">
                    This send predates recipient tracking. Use Update Email &amp; Resend and state the address.
                  </p>
                )}
              </div>
            </div>
            {/* "Retry Same Address" stood here and was removed in #TBD. It
                called `resendCustomerEmail(id)`, which takes NO body — the
                server then re-derived the recipient from the job/customer
                lookup chain. It was a fresh lookup wearing a retry label, and
                on a bounce the lookup lands somewhere else BY CONSTRUCTION:
                the address that just bounced is the one the chain now walks
                past. TNT FT-04444, 2026-09-09 — two presses of it sent the
                ticket to the consultant who had just been replaced, and he
                opened it.

                It is not relabelled, because the screen cannot name the
                address it would retry: `customer_email_sent_to` does not
                exist as a column (see the banner text above, which falls
                through to the customer record for the same reason). A control
                whose label asserts something the code never computes is worse
                than no control — so the only affordance left on a bounce is
                the one that makes the office state the recipient. */}
            {!isSales && (
            <div className="flex gap-3">
              <button
                onClick={() => setEmailCorrectionOpen(true)}
                className="fi-action fi-action--standard fi-action--primary"
              >
                <Mail size={14} /> Update Email &amp; Resend
              </button>
              {/* A RETRY, meaning this exact address — which is why it renders
                  only when the address is known, and why it names it instead of
                  asserting a sameness the code does not compute.

                  It stood here as "Retry Same Address" calling
                  `resendCustomerEmail(id)` with NO body, which hands the
                  recipient back to the server's job → customer lookup chain;
                  two presses sent FT-04444 to the previous consultant. #602
                  deleted it, correctly, on the reasoning that the screen could
                  not name the address it would retry — there was no column
                  holding it. API 0391 is that column, so the control comes back
                  able to keep its promise.

                  `force: true` is required, not optional. The address is on the
                  provider's account-wide suppression list by definition — it is
                  what put this banner on screen — so the pre-send guard refuses
                  it without force, and a retry that cannot reach the address it
                  names is the same defect again. The forced send is a real
                  attempt; if the mailbox is still dead the provider drops it and
                  the banner comes back. */}
              {ticket.customerEmailSentTo && (
                <Tooltip content={`Send again to ${ticket.customerEmailSentTo}`}>
                  <button
                    onClick={() => resendEmailMutation.mutate({ email: ticket.customerEmailSentTo!, force: true })}
                    disabled={resendEmailMutation.isPending}
                    className="flex items-center gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-2 text-sm font-medium text-fieldiq-text transition-colors hover:text-fieldiq-accent disabled:opacity-50"
                  >
                    {resendEmailMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Retry {ticket.customerEmailSentTo}
                  </button>
                </Tooltip>
              )}
            </div>
            )}
          </div>
        )}
        {ticket.customerEmailStatus === 'complained' && (
          <div className="space-y-2 rounded-fi-panel border border-fieldiq-danger/40 bg-fieldiq-danger/10 p-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className="text-fieldiq-text" aria-hidden="true" />
              <p className="text-sm font-medium text-white">Customer marked this email as spam</p>
            </div>
            {/* Carries the address for the same reason the bounce banner's
                retry does. A complaint does not suppress the address, so a
                body-less call here would not be refused — it would quietly
                resolve to whoever the job/customer chain names next, which is
                the failure mode being fixed, not a safe one. */}
            {isSales ? null : ticket.customerEmailSentTo ? (
              <button
                onClick={() => resendEmailMutation.mutate({ email: ticket.customerEmailSentTo!, force: true })}
                disabled={resendEmailMutation.isPending}
                className="flex items-center gap-2 rounded-fi-control border border-fieldiq-danger/40 bg-fieldiq-card px-4 py-2 text-sm font-medium text-fieldiq-text transition-colors hover:text-fieldiq-accent disabled:opacity-50"
              >
                {resendEmailMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Send again to {ticket.customerEmailSentTo}
              </button>
            ) : (
              <p className="text-xs text-fieldiq-muted">Recipient not recorded — use Send to another address.</p>
            )}
          </div>
        )}

        {/* The approval link from this browser's last send or resend. Shown
            whatever the email's status: after a FAILED send it is the way to
            get the link to the customer by hand. */}
        {sendApprovalUrl && (
          <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-3 space-y-2">
            <p className="text-xs text-fieldiq-muted">Customer Approval Link</p>
            <div className="flex gap-2">
              <input type="text" readOnly aria-label="Customer approval link" value={sendApprovalUrl} className="flex-1 bg-fieldiq-dark rounded-fi-control border border-fieldiq-border px-3 py-1.5 text-xs text-fieldiq-text" />
              <button
                aria-label="Copy the customer approval link"
                onClick={() => { navigator.clipboard.writeText(sendApprovalUrl); showToast('Copied to clipboard'); }}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark p-2 text-fieldiq-text transition-colors hover:text-fieldiq-accent"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        )}

        {/* THE TICKET'S OWN RECORD OF THE JOB.
            EVERY FIELD RENDERS, AND AN ABSENT ONE IS AN EM DASH. Rig, LSD,
            well, AFE and well licence were each wrapped in a truthiness test,
            so a ticket missing one simply had no row for it — and a reader
            cannot tell "this ticket records no well" from "this screen does
            not show wells". On a page whose whole job is to be the record of
            what was done, the gap has to be visible. These are also the ten
            columns the move-job guard compares against a destination job, so
            an office user sent here by a SNAPSHOT_CONFLICT has to be able to
            see which of them is blank. */}
        <RecordPanel eyebrow="Ticket record" title={ticket.customer}>
          <div className="space-y-4 p-4">
            {(ticket.customerContactName || ticket.customerAddress) && (
              <div className="space-y-0.5">
                {ticket.customerContactName && (
                  <p className="flex items-center gap-1.5 text-sm text-fieldiq-text">
                    <User size={11} className="text-fieldiq-muted" aria-hidden="true" /> {ticket.customerContactName}
                    {ticket.customerContactPhone && <span className="text-fieldiq-muted">· {ticket.customerContactPhone}</span>}
                  </p>
                )}
                {ticket.customerAddress && (
                  <p className="flex items-center gap-1.5 text-sm text-fieldiq-text">
                    <MapPin size={11} className="text-fieldiq-muted" aria-hidden="true" /> {ticket.customerAddress}
                  </p>
                )}
              </div>
            )}
            <dl className="grid grid-cols-2 gap-4 lg:grid-cols-3">
              {[
                { icon: FileText, label: 'Job', value: ticket.jobNumber },
                { icon: Calendar, label: 'Date', value: fmtDate(ticket.date) },
                { icon: Wrench, label: 'Service', value: getJobTypeLabel(ticket.serviceType) },
                { icon: Wrench, label: 'Rig', value: ticket.rigName },
                { icon: MapPin, label: 'Location', value: ticket.lsd },
                { icon: MapPin, label: 'Well', value: ticket.wellName },
                { icon: FileText, label: 'AFE / PO #', value: ticket.afeNumber },
                { icon: FileText, label: 'Well licence', value: ticket.wellLicence },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label}>
                  <dt className="mb-1 flex items-center gap-1.5 text-xs text-fieldiq-muted">
                    <Icon size={11} aria-hidden="true" /> {label}
                  </dt>
                  <dd className={`text-sm font-medium ${value ? 'text-white' : 'text-fieldiq-muted'}`}>
                    {value || '\u2014'}
                  </dd>
                </div>
              ))}
              <div>
                <dt className="mb-1 flex items-center gap-1.5 text-xs text-fieldiq-muted">
                  <Clock size={11} aria-hidden="true" /> Hours
                </dt>
                {/* THREE ANSWERS, NOT TWO. A figure, a contradiction, and "the
                    server did not compute one" are different facts, and only
                    the last of them is an absence. */}
                <dd>
                  {ticket.hours ? (
                    <span className="text-sm font-medium text-white">{ticket.hours} hrs</span>
                  ) : ticket.hoursContradictory ? (
                    /* THE WORDS ARE THE REGISTER'S, VERBATIM, and they have to
                       stay that way: TicketsScreen.parity.test.tsx renders the
                       list and this page over one payload precisely so the two
                       cannot describe the same ticket differently. The SKIN is
                       what changed — a tint and a glyph carrying the alarm, with
                       the theme's own ink on top, because the bright literal red
                       this replaced went to about 3:1 on a white panel. */
                    <span className="inline-flex w-fit items-center gap-1.5 rounded-fi-pill border border-fieldiq-danger/40 bg-fieldiq-danger/10 px-2 py-1 text-xs font-semibold text-white">
                      <AlertTriangle size={11} className="text-fieldiq-text" aria-hidden="true" />
                      {/* One text node, so the phrase the parity test compares
                          is not split across elements by the glyph. */}
                      <span>Times contradict — finish before arrival</span>
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-fieldiq-muted">Not calculated</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </RecordPanel>

        {/* Company Info */}
        {(ticket.companyName || ticket.companyAddress) && (
          <div className="flex items-start gap-3 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
            <Building2 size={16} className="mt-0.5 flex-shrink-0 text-fieldiq-accent-signal" aria-hidden="true" />
            <div>
              {ticket.companyName && <p className="text-sm font-semibold text-white">{ticket.companyName}</p>}
              {ticket.companyAddress && <p className="text-sm text-fieldiq-text">{ticket.companyAddress}</p>}
              {ticket.companyPhone && <p className="text-sm text-fieldiq-text">{ticket.companyPhone}</p>}
            </div>
          </div>
        )}

        {/* Crew */}
        {ticket.crew.length > 0 && (
          <RecordPanel eyebrow="On this ticket" title="Crew" count={ticket.crew.length}>
            <div className="divide-y divide-fieldiq-border">
              {ticket.crew.map((member: any, i: number) => (
                <div key={i} className="px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-fieldiq-accent/20 flex items-center justify-center text-xs font-bold text-fieldiq-accent">{member.initials}</div>
                  <div><p className="text-sm font-medium text-white">{member.name}</p><p className="text-sm text-fieldiq-text">{crewRoleLabel(member)}</p></div>
                </div>
              ))}
            </div>
          </RecordPanel>
        )}

        {/* Equipment */}
        {ticket.equipment.length > 0 && (
          <RecordPanel eyebrow="On this ticket" title="Equipment" count={ticket.equipment.length}>
            <div className="divide-y divide-fieldiq-border">
              {ticket.equipment.map((eq: any, i: number) => (
                <div key={i} className="px-4 py-3 flex items-center justify-between">
                  <div><p className="text-sm font-medium text-white">{eq.name}</p><p className="text-xs text-fieldiq-muted">{eq.type}{eq.serial_number ? ` · ${eq.serial_number}` : ''}</p></div>
                  {/* The hues are the equipment register's (EquipmentScreen's
                      STATUS_BADGE): green means free, blue means committed to a
                      job. This page had them the other way round — `available`
                      blue and `in_use` green — so the same asset read as two
                      different colours on two screens. Anything outside those
                      three keeps the neutral outlined pill and its own raw
                      label; it is not guessed into one of them. */}
                  {eq.status && (
                      <span className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill px-2 py-1 text-[0.62rem] font-black uppercase leading-none tracking-[0.06em] ${
                        eq.status === 'in_use' || eq.status === 'reserved'
                          ? 'border border-black/20 bg-fi-signal-blue text-fi-signal-ink'
                          : eq.status === 'available'
                            ? 'border border-black/20 bg-fi-signal-green text-fi-signal-ink'
                            : 'border border-fieldiq-border text-fieldiq-text'
                      }`}>
                        {eq.status.replace(/_/g, ' ')}
                      </span>
                  )}
                </div>
              ))}
            </div>
          </RecordPanel>
        )}

        {/* Line Items */}
        {ticket.lineItems.length > 0 && (
          <RecordPanel eyebrow="What is being billed" title="Line items" count={ticket.lineItems.length}>
              {/* `min-w-` so narrow viewports scroll rather than crush the
                  three money columns into each other. */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[34rem] text-sm">
                  <thead>
                    <tr className="border-b border-fieldiq-border text-[0.65rem] font-black uppercase tracking-[0.1em] text-fieldiq-muted">
                      <th scope="col" className="px-4 py-2.5 text-left">Description</th>
                      <th scope="col" className="px-4 py-2.5 text-right">Qty</th>
                      <th scope="col" className="px-4 py-2.5 text-right">Rate</th>
                      <th scope="col" className="px-4 py-2.5 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-fieldiq-border">
                    {ticket.lineItems.map((item: any, i: number) => (
                      <tr key={item.id || i}>
                        <td className="px-4 py-2.5 text-white">
                          {item.description || fmtLineItemType(item.line_item_type || item.lineItemType || '')}
                          {item.unit && <span className="text-fieldiq-muted ml-1">({fmtUnit(item.unit)})</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right text-fieldiq-text">{fmtQty(item.quantity)}</td>
                        <td className="px-4 py-2.5 text-right text-fieldiq-text">{fmtCurrency(item.rate)}</td>
                        <td className="px-4 py-2.5 text-right text-white font-medium">{fmtCurrency(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          </RecordPanel>
        )}

        {/* Billing */}
        <RecordPanel eyebrow="What the customer owes" title="Billing">
          <div className="space-y-2 p-4">
            <div className="flex justify-between text-sm"><span className="text-fieldiq-text">Subtotal</span><span className="text-white">{fmtCurrency(ticket.subtotal)} {currency}</span></div>
            {ticket.taxRate1Label && (ticket.taxRate1Amount ?? 0) > 0 && (
              <div className="flex justify-between text-sm"><span className="text-fieldiq-text">{ticket.taxRate1Label}</span><span className="text-white">{fmtCurrency(ticket.taxRate1Amount)} {currency}</span></div>
            )}
            {ticket.taxRate2Label && (ticket.taxRate2Amount ?? 0) > 0 && (
              <div className="flex justify-between text-sm"><span className="text-fieldiq-text">{ticket.taxRate2Label}</span><span className="text-white">{fmtCurrency(ticket.taxRate2Amount)} {currency}</span></div>
            )}
            <div className="mt-2 flex justify-between border-t border-fieldiq-border pt-2">
              <span className="text-sm font-bold text-white">Total</span>
              <span className="text-lg font-bold text-white">{fmtCurrency(ticket.total)} {currency}</span>
            </div>
          </div>
        </RecordPanel>

        {ticket.createdBy && <p className="text-center text-sm text-fieldiq-muted">Created by {ticket.createdBy}</p>}
        </div>
      </section>

      {/* Send to Customer Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowSendModal(false)}>
          <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card w-full max-w-md shadow-2xl p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-1">Send for Customer Approval</h3>
            <p className="text-sm text-fieldiq-text mb-4">Enter the email to send the approval link to.</p>
            <div className="space-y-4">
              <SendModalSavedContacts
                rows={sendContactsQuery.data}
                isLoading={sendContactsQuery.isLoading}
                error={sendContactsQuery.error}
                rigName={sendRigName}
                currentAddress={sendAddress}
                onPick={(c) => { setSendRigEmail((c.email ?? '').trim()); setSendContactName((c.name ?? '').trim()); }}
              />
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block font-medium">Rig Email {sendRigName ? `(${sendRigName})` : ''}</label>
                <input type="email" value={sendRigEmail} onChange={e => setSendRigEmail(e.target.value)} placeholder="Rig consultant email..." className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-white outline-none placeholder-fieldiq-muted focus:border-fi-amber" />
                {/* Single-job since A3 (api#841): `saveRigEmail` writes THIS
                    ticket's job and no other. The old label, "preferred email
                    for" the rig, promised a rig-wide default it no longer sets. */}
                {sendRigName && (
                  <>
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input type="checkbox" checked={saveAsRigEmail} onChange={e => setSaveAsRigEmail(e.target.checked)} className="w-4 h-4 rounded" />
                      <span className="text-xs text-fieldiq-text">Use this address for this job's approval emails</span>
                    </label>
                    <p className="mt-1 ml-6 text-xs text-fieldiq-muted">
                      Other jobs on {sendRigName} aren't changed. Save the contact below to reuse it on new jobs.
                    </p>
                  </>
                )}
              </div>
              <div>
                <label className="text-xs text-fieldiq-muted mb-1 block font-medium">Customer Email</label>
                <input type="email" value={sendCustomerEmail} onChange={e => setSendCustomerEmail(e.target.value)} placeholder="Customer company email..." className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-white outline-none placeholder-fieldiq-muted focus:border-fi-amber" />
              </div>
              <div>
                <label htmlFor="send-contact-name" className="text-xs text-fieldiq-muted mb-1 block font-medium">
                  Recipient name {saveContactRequested ? '' : '(optional)'}
                </label>
                <input
                  id="send-contact-name"
                  type="text"
                  value={sendContactName}
                  maxLength={100}
                  onChange={e => setSendContactName(e.target.value)}
                  placeholder="Who this is going to..."
                  aria-invalid={saveContactRequested && !sendContactName.trim() ? true : undefined}
                  aria-describedby={saveContactRequested && !sendContactName.trim() ? 'send-contact-name-needed' : undefined}
                  className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-white outline-none placeholder-fieldiq-muted focus:border-fi-amber"
                />
                {offerSaveContact && (
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input type="checkbox" checked={saveAsContact} onChange={e => setSaveAsContact(e.target.checked)} className="w-4 h-4 rounded" />
                    <span className="text-xs text-fieldiq-text">Save as customer contact</span>
                  </label>
                )}
                {saveContactRequested && !sendContactName.trim() && (
                  <p id="send-contact-name-needed" role="status" className="mt-1 text-xs text-fieldiq-text">
                    Add a name to save this contact.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowSendModal(false)} className="flex-1 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark py-2.5 text-sm font-medium text-white">Cancel</button>
              <button
                disabled={sendLoading || (!sendRigEmail.trim() && !sendCustomerEmail.trim()) || (saveContactRequested && !sendContactName.trim())}
                onClick={() => { void sendToCustomer({}); }}
                className="fi-action fi-action--standard fi-action--primary flex-1 justify-center disabled:opacity-40"
              >
                <Send size={14} /> {sendLoading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move to another job */}
      <MoveTicketJobModal
        open={moveJobOpen}
        onClose={() => setMoveJobOpen(false)}
        ticket={{
          id: ticket.id,
          ticketNumber: ticket.ticketNumber,
          jobId: ticket.jobId,
          jobNumber: ticket.jobNumber,
          customerId: ticket.customerId,
          customer: ticket.customer,
        }}
        onMoved={(result) => {
          setMoveJobOpen(false);
          setMoveResult(result);
          // The row the API handed back IS the post-move, post-revert ticket —
          // written in-place so the header buttons follow the new status
          // without a reopen. `invalidateTickets` then refetches the truth.
          if (result.ticket?.id) {
            setQueryDataMapped(queryClient, ['ticket-detail', ticketId], mapTicket(result.ticket));
          }
          // Per api#771's cache note: the ticket, the ticket lists, and BOTH
          // jobs — the source loses a ticket and the destination gains one, so
          // invalidating only the destination leaves the old job's list wrong.
          invalidateTickets(queryClient);
          queryClient.invalidateQueries({ queryKey: ['jobs'] });
          queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
          queryClient.invalidateQueries({ queryKey: ['job', result.move.fromJobId] });
          queryClient.invalidateQueries({ queryKey: ['job', result.move.toJobId] });
        }}
      />

      {/* Customer Email Prompt Modal */}
      <CustomerEmailPromptModal
        open={emailPromptOpen}
        onClose={() => setEmailPromptOpen(false)}
        ticket={ticket}
        onSent={(d) => {
          if (d?.id) setQueryDataMapped(queryClient, ['ticket-detail', ticketId], mapTicket(d));
          invalidateTickets(queryClient);
          showToast('Email sent — awaiting customer approval');
        }}
        onToast={showToast}
      />

      {/* Email Correction Modal (for bounced/failed) */}
      <CustomerEmailPromptModal
        open={emailCorrectionOpen}
        onClose={() => setEmailCorrectionOpen(false)}
        ticket={ticket}
        /* The recorded recipient, or nothing. It used to fall through to
           `customerContactEmail` — the CUSTOMER RECORD's address — so the modal
           that exists to CORRECT a failed address opened pre-filled with an
           address that had not failed, and one press sent the ticket there.
           When the recipient is unknown the office states it; an empty field
           asks the question, a wrong default answers it. */
        initialEmail={ticket.customerEmailSentTo || ''}
        title="Update Email & Resend"
        description="The previous delivery failed. Correct the address and resend the approval link."
        onSent={(d) => {
          if (d?.id) setQueryDataMapped(queryClient, ['ticket-detail', ticketId], mapTicket(d));
          invalidateTickets(queryClient);
          showToast('Email sent — awaiting customer approval');
        }}
        onToast={showToast}
      />

      {attachOpen && (
        <AttachSignedCopyDialog
          ticketId={ticket.id}
          ticketNumber={ticket.ticketNumber || null}
          status={ticket.status}
          timeZone={timeZone}
          onClose={() => setAttachOpen(false)}
          onAttached={(m) => { setAttachOpen(false); showToast(m); }}
        />
      )}

      {/* Reject Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card w-full max-w-sm shadow-2xl animate-fade-in p-5">
            <h3 className="text-lg font-bold text-white mb-2">Reject Ticket</h3>
            <p className="text-sm text-fieldiq-text mb-4">Provide a reason so the field worker knows what to fix.</p>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="e.g. Incorrect metres, wrong rate applied..." rows={3} className="mb-4 w-full resize-none rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-white focus:border-fi-amber focus:outline-none" autoFocus />
            <div className="flex gap-3">
              <button onClick={() => { setShowRejectModal(false); setRejectReason(''); }} className="flex-1 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark py-2.5 text-sm font-medium text-white">Cancel</button>
              <button onClick={() => rejectMutation.mutate(rejectReason)} disabled={rejectMutation.isPending || !rejectReason.trim()} className="flex flex-1 items-center justify-center gap-2 rounded-fi-control border border-black/20 bg-fi-signal-red py-2.5 text-sm font-bold text-fi-signal-ink disabled:opacity-50">
                {rejectMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
